'use client';

import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '../utils/cn';

const Drawer = ({
  // Ionic/Capacitor already uses transforms; Vaul's scale leaves a side gap
  // and lets the tab-bar avatar paint beside the sheet.
  shouldScaleBackground = false,
  // Disable vaul's built-in keyboard handling: it reacts to `visualViewport`
  // resize events and writes `style.bottom`/`style.height` directly to the DOM,
  // which both (a) is unreliable on iOS Capacitor with `Keyboard.resize: 'none'`
  // and (b) fights with the React inline style we set on `DrawerContent`. Our
  // own keyboard-aware positioning lives in `DrawerContent` + `useKeyboardInset`.
  repositionInputs = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    repositionInputs={repositionInputs}
    {...props}
  />
);
Drawer.displayName = 'Drawer';

const KEYBOARD_CSS_VAR = '--keyboard-inset-height';

function parsePxVar(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const num = Number.parseFloat(trimmed);
  return Number.isFinite(num) ? Math.max(0, num) : 0;
}

function useKeyboardInset(active: boolean): number {
  const [inset, setInset] = React.useState(0);

  React.useEffect(() => {
    if (!active) return;
    if (typeof window === 'undefined') return;

    const vv = window.visualViewport;
    const root = document.documentElement;

    let rafId: number | null = null;
    const compute = () => {
      rafId = null;
      const vvInset = vv
        ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
        : 0;
      const cssInset = parsePxVar(
        getComputedStyle(root).getPropertyValue(KEYBOARD_CSS_VAR),
      );
      const next = Math.max(vvInset, cssInset);
      setInset((prev) => (prev === next ? prev : next));
    };

    const schedule = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(compute);
    };

    compute();

    vv?.addEventListener('resize', schedule);
    vv?.addEventListener('scroll', schedule);

    const mo = new MutationObserver(schedule);
    mo.observe(root, { attributes: true, attributeFilter: ['style'] });

    return () => {
      vv?.removeEventListener('resize', schedule);
      vv?.removeEventListener('scroll', schedule);
      mo.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [active]);

  return active ? inset : 0;
}

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>((props, ref) => {
  const overlayProps = {
    ...props,
    // Above Ionic/Capacitor chrome (tab bar, ion-page stacks often sit near z-50).
    className: 'fixed inset-0 z-[10000] h-full w-full bg-black/80',
    'data-app-overlay': '',
  } as React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> & {
    className: string;
    'data-app-overlay': string;
  };
  return <DrawerPrimitive.Overlay ref={ref} {...overlayProps} />;
});
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

interface DrawerContentProps extends React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Content
> {
  children?: React.ReactNode;
  className?: string;
  scrollable?: boolean;
  fitContent?: boolean;
  viewportClassName?: string;
  keyboardAware?: boolean;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      scrollable = true,
      fitContent = true,
      viewportClassName,
      keyboardAware = true,
      style,
      ...props
    },
    ref,
  ) => {
    const keyboardInset = useKeyboardInset(keyboardAware);
    const hasKeyboard = keyboardInset > 0;

    const keyboardStyle: React.CSSProperties = hasKeyboard
      ? {
          bottom: `${keyboardInset}px`,
          maxHeight: `calc(100dvh - ${keyboardInset}px - 2.5rem)`,
          paddingBottom: 0,
          transition:
            'bottom 180ms ease-out, max-height 180ms ease-out, padding-bottom 180ms ease-out',
        }
      : {
          transition:
            'bottom 160ms ease-out, max-height 160ms ease-out, padding-bottom 160ms ease-out',
        };

    const contentProps = {
      ...props,
      style: {
        ...style,
        ...keyboardStyle,
      } as React.CSSProperties,
      className: cn(
        'fixed inset-x-0 bottom-0 left-0 right-0 z-[10000] flex w-full max-w-none flex-col overflow-hidden rounded-t-[10px] border bg-background shadow-lg',
        'pb-[max(1rem,env(safe-area-inset-bottom))]',
        fitContent
          ? 'h-auto max-h-[min(90vh,calc(100dvh-2.5rem))]'
          : 'h-[min(90vh,calc(100dvh-2.5rem))] max-h-[min(90vh,calc(100dvh-2.5rem))]',
        className,
      ),
    };
    return (
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerPrimitive.Content
          ref={ref}
          {...(contentProps as unknown as React.ComponentPropsWithoutRef<
            typeof DrawerPrimitive.Content
          >)}
        >
          <DrawerPrimitive.Handle className="mx-auto mt-4 h-2 w-[100px] flex-shrink-0 rounded-full bg-muted" />
          <div
            className={cn(
              'flex min-h-0 flex-col px-4',
              !fitContent && 'flex-1',
              scrollable
                ? 'overflow-y-auto overscroll-contain'
                : 'overflow-hidden',
              viewportClassName,
            )}
          >
            {children}
          </div>
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 p-4 -mx-4 text-center sm:text-left', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4 -mx-4', className)}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

interface DrawerTitleProps extends React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Title
> {
  children?: React.ReactNode;
  className?: string;
}

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  DrawerTitleProps
>(({ className, ...props }, ref) => {
  const titleProps = {
    ...props,
    className: cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    ),
  } as React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
    className?: string;
  };
  return <DrawerPrimitive.Title ref={ref} {...titleProps} />;
});
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

interface DrawerDescriptionProps extends React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Description
> {
  children?: React.ReactNode;
  className?: string;
}

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  DrawerDescriptionProps
>(({ className, ...props }, ref) => {
  const descriptionProps = {
    ...props,
    className: cn('text-sm text-muted-foreground', className),
  };
  return (
    <DrawerPrimitive.Description
      ref={ref}
      {...(descriptionProps as unknown as React.ComponentPropsWithoutRef<
        typeof DrawerPrimitive.Description
      >)}
    />
  );
});
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
